import * as React from 'react';

import type {
  AIMessageDraft,
  AudienceDelta,
  CohortAudience,
} from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import { useCohortCreate } from 'bundles/enterprise-admin-messaging-v2/hooks/useCohortCreate';
import { useCohortLearnerPaging } from 'bundles/enterprise-admin-messaging-v2/hooks/useCohortLearnerPaging';
import {
  CONTROL_PLANE_ERROR_CODE,
  NLQ_TOO_LONG_MESSAGE_PATTERN,
} from 'bundles/enterprise-admin-messaging-v2/utils/constants';

import _t from 'i18n!nls/enterprise-admin-messaging-v2';

export type CohortFlowStatus = 'idle' | 'creating' | 'success' | 'disambiguation' | 'error';

export type CohortSendArgs = {
  orgId: string;
  cohortId: string;
  preset: string;
  callToAction: string;
  audienceDelta?: AudienceDelta;
};

type Params = {
  open: boolean;
  prompt: string;
  orgId: string;
  programIds?: string[];
  presetCohortAudience?: CohortAudience;
  updateDraft: (partial: Partial<AIMessageDraft>) => void;
};

type Result = {
  /**
   * Fires the NLQ cohort creation. Resolves `true` on success, `false` on any
   * failure. Pass `promptOverride` to use a prompt directly instead of the
   * ref-mirrored `draft.prompt` — needed by the auto-generate-on-open flow,
   * where `draft.prompt` hasn't been synced to the ref yet on the first render.
   */
  createCohort: (promptOverride?: string) => Promise<boolean>;
  status: CohortFlowStatus;
  cohortId: string | null;
  isCreating: boolean;
  errorMessage: string | null;
  /** Custom error title for cases like empty cohort; overrides the generic title in the modal. */
  errorTitle: string | null;
  /** Raw `error.code` from the cohort API (e.g. `OUT_OF_SCOPE`); lets the modal render code-specific content like links. */
  errorCode: string | null;
  warnings: string[];
  cohortAudience: CohortAudience | undefined;
  resolveSendArgs: (ctaText: string, callToAction?: string, audienceDelta?: AudienceDelta) => CohortSendArgs | null;
  dismissError: () => void;
};

/**
 * Business-logic orchestrator for the AI cohort flow. Owns the full cohort
 * lifecycle: creation via NLQ, status handling (SUCCESS / DISAMBIGUATION /
 * ERROR), draft updates, and exposes `cohortAudience` for the learner-list
 * screens and `resolveSendArgs` for the send path.
 *
 * Extracted from `AIMessageModal` so the modal stays thin (UI wiring only)
 * and this logic is independently unit-testable.
 */
export const useAICohortFlow = ({
  open,
  prompt,
  orgId,
  programIds,
  presetCohortAudience,
  updateDraft,
}: Params): Result => {
  const [status, setStatus] = React.useState<CohortFlowStatus>('idle');
  const [cohortId, setCohortId] = React.useState<string | null>(null);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [errorTitle, setErrorTitle] = React.useState<string | null>(null);
  const [errorCode, setErrorCode] = React.useState<string | null>(null);
  const [warnings, setWarnings] = React.useState<string[]>([]);

  const { createCohort: mutateCreateCohort, loading: isCreating } = useCohortCreate();

  // Stable ref to the latest updateDraft so the async callback never closes
  // over a stale version (the parent re-creates it on every render via useCallback).
  const updateDraftRef = React.useRef(updateDraft);
  React.useEffect(() => {
    updateDraftRef.current = updateDraft;
  }, [updateDraft]);

  // Stable ref for prompt so createCohort reads the latest at call-time.
  const promptRef = React.useRef(prompt);
  React.useEffect(() => {
    promptRef.current = prompt;
  }, [prompt]);

  // Guard against duplicate in-flight calls (e.g. rapid Generate clicks).
  const isCreatingRef = React.useRef(false);

  // Reset all internal state when the modal closes.
  React.useEffect(() => {
    if (!open) {
      setStatus('idle');
      setCohortId(null);
      setErrorMessage(null);
      setErrorTitle(null);
      setErrorCode(null);
      setWarnings([]);
      isCreatingRef.current = false;
    }
  }, [open]);

  React.useEffect(() => {
    if (!open || !presetCohortAudience) return;
    setStatus('success');
    setCohortId(presetCohortAudience.cohortId);
  }, [open, presetCohortAudience]);

  const createCohort = React.useCallback(
    async (promptOverride?: string): Promise<boolean> => {
      if (presetCohortAudience) return true;

      const currentPrompt = promptOverride ?? promptRef.current;
      if (!currentPrompt || !orgId || isCreatingRef.current) return false;

      isCreatingRef.current = true;

      setStatus('creating');
      setErrorMessage(null);
      setErrorTitle(null);
      setErrorCode(null);
      setWarnings([]);

      const result = await mutateCreateCohort(currentPrompt, orgId, programIds);
      isCreatingRef.current = false;

      switch (result.status) {
        case 'SUCCESS': {
          if (result.learners.length === 0 && (result.cohortSize ?? 0) === 0) {
            setStatus('error');
            setErrorTitle(_t('No learners match this audience'));
            setErrorMessage(
              _t('Your criteria are very specific. Here are closer matches in your organization you could try instead.')
            );
            return false;
          }
          setStatus('success');
          setCohortId(result.cohortId);
          setWarnings(result.warnings);
          updateDraftRef.current({
            cohortId: result.cohortId,
            resolvedProgramIds: result.resolvedProgramIds,
            learners: result.learners,
            originalLearners: result.learners,
            audienceCount: result.cohortSize ?? result.learners.length,
            resolvedTimeRange: result.resolvedTimeRange,
          });
          return true;
        }
        case 'DISAMBIGUATION_NEEDED': {
          setStatus('disambiguation');
          setWarnings(result.warnings);
          setErrorTitle(_t('Tell me a bit more'));
          setErrorMessage(_t('Who should receive this, and what do you want them to do?'));
          return false;
        }
        case 'ERROR': {
          setStatus('error');
          if (result.errorCode === CONTROL_PLANE_ERROR_CODE.DISAMBIGUATION_REQUIRED) {
            setErrorTitle(_t('Tell me a bit more'));
            setErrorMessage(_t('Who should receive this, and what do you want them to do?'));
          } else if (result.errorCode === CONTROL_PLANE_ERROR_CODE.OUT_OF_SCOPE) {
            setErrorTitle(_t('This channel works best for learning communications'));
            setErrorCode(CONTROL_PLANE_ERROR_CODE.OUT_OF_SCOPE);
          } else if (
            result.errorCode === CONTROL_PLANE_ERROR_CODE.INVALID_ARGUMENTS &&
            NLQ_TOO_LONG_MESSAGE_PATTERN.test(result.errorMessage)
          ) {
            setErrorTitle(_t('Something went wrong on our end.'));
            setErrorMessage(_t('Try making the prompt shorter.'));
          } else {
            setErrorTitle(_t('Something went wrong on our end.'));
            setErrorMessage(_t('Please try again.'));
          }
          return false;
        }
        default:
          return false;
      }
    },
    [orgId, programIds, presetCohortAudience, mutateCreateCohort]
  );

  const cohortAudience: CohortAudience | undefined = React.useMemo(() => {
    if (presetCohortAudience) return presetCohortAudience;
    if (!cohortId || !orgId) return undefined;
    return { kind: 'cohort', cohortId, orgId };
  }, [cohortId, orgId, presetCohortAudience]);

  const { learners: presetLearners, total: presetTotal } = useCohortLearnerPaging({
    cohortAudience: presetCohortAudience,
    page: 1,
    skip: !presetCohortAudience,
  });

  React.useEffect(() => {
    if (!open || !presetCohortAudience) return;
    updateDraftRef.current({
      cohortId: presetCohortAudience.cohortId,
      resolvedProgramIds: [],
      learners: presetLearners,
      originalLearners: presetLearners,
      audienceCount: presetTotal,
      resolvedTimeRange: null,
    });
  }, [open, presetCohortAudience, presetLearners, presetTotal]);

  const dismissError = React.useCallback(() => {
    setStatus('idle');
    setErrorMessage(null);
    setErrorTitle(null);
    setErrorCode(null);
  }, []);

  const resolveSendArgs = React.useCallback<
    (ctaText: string, callToAction?: string, audienceDelta?: AudienceDelta) => CohortSendArgs | null
  >(
    (ctaText: string, callToAction?: string, audienceDelta?: AudienceDelta): CohortSendArgs | null => {
      if (!cohortAudience) return null;
      const hasCta = ctaText.trim().length > 0;

      let resolvedCallToAction: string;
      if (!hasCta) {
        resolvedCallToAction = 'DO_NOT_INCLUDE';
      } else if (callToAction && callToAction !== 'DO_NOT_INCLUDE') {
        // Use the CTA the generation API actually classified for this prompt.
        resolvedCallToAction = callToAction;
      } else {
        // Fall back to FIND_A_COURSE when there's no classified CTA, or when
        // it's a stale DO_NOT_INCLUDE from a generation the admin has since
        // overridden by typing a label — an explicit label always gets a button.
        resolvedCallToAction = 'FIND_A_COURSE';
      }

      return {
        orgId: cohortAudience.orgId,
        cohortId: cohortAudience.cohortId,
        preset: 'custom',
        callToAction: resolvedCallToAction,
        audienceDelta,
      };
    },
    [cohortAudience]
  );

  return {
    createCohort,
    status,
    cohortId,
    isCreating,
    errorMessage,
    errorTitle,
    errorCode,
    warnings,
    cohortAudience,
    resolveSendArgs,
    dismissError,
  };
};

export default useAICohortFlow;
